import { Button } from '@amore/ui/components/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@amore/ui/components/tooltip'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { ArrowRight, BookOpen, Plus } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { plural } from '@/lib/format'
import { useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { Supplier, WeddingData } from '@/mock/types'
import { type Column, DataTable, type TableGroup } from '@/patterns/data-table'
import { EmptyState } from '@/patterns/empty-state'
import { FilterBar } from '@/patterns/filter-bar'
import { linkedModuleConfig } from '@/patterns/linked-record'
import { PromptList, useAddPromptTask } from '@/patterns/prompts'
import { Screen } from '@/patterns/screen'
import { SectionTabs } from '@/patterns/section-tabs'
import { StatSummary } from '@/patterns/stat-summary'
import { AddSupplierSheet } from './add-sheet'
import suppliersIllustration from './assets/suppliers.svg'
import { CatalogContent } from './catalog'
import {
  categoryLabel,
  categoryOrder,
  filterLabels,
  isAgreed,
  matchesFilter,
  matchesSearch,
  parseFilter,
  type SupplierFilter,
  type SupplierPromptAction,
  supplierFilters,
  supplierPrompts,
  supplierUsage,
} from './model'
import { type SheetTab, SupplierSheet, sheetTabs } from './supplier-sheet'
import { SupplierStatus } from './supplier-status'

const tabPaths = { mine: '/suppliers', catalog: '/suppliers/catalog' }

/** Dodavatelé is one page with two tabs, like Hosté: Moji dodavatelé and Katalog. */
function SuppliersPage({
  tab,
  actions,
  children,
}: {
  tab: keyof typeof tabPaths
  actions?: ReactNode
  children?: ReactNode
}) {
  return (
    <Screen
      title="Dodavatelé"
      illustration={<img src={suppliersIllustration} alt="" className="h-auto w-full" />}
      description="Se kým pro svatbu jednáte, jak daleko jste a koho ještě najít."
      actions={actions}
      tabs={
        <SectionTabs
          label="Sekce dodavatelů"
          active={tabPaths[tab]}
          tabs={[
            { to: tabPaths.mine, label: 'Moji dodavatelé' },
            { to: tabPaths.catalog, label: 'Katalog' },
          ]}
        />
      }
    >
      {children}
    </Screen>
  )
}

/** Categories offered as a first step on an empty list. */
const starterCategories = ['venue', 'photographer', 'catering', 'officiant', 'party-music']

export function SuppliersScreen() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const navigate = useNavigate()
  const addTask = useAddPromptTask()
  const search = useSearch({ strict: false }) as { filter?: string; q?: string; tab?: string }
  const { supplierId } = useParams({ strict: false }) as { supplierId?: string }
  const filter = parseFilter(search.filter)
  const query = search.q ?? ''
  const listSearch = {
    ...(filter !== 'all' ? { filter } : {}),
    ...(query ? { q: query } : {}),
  }

  const setSearch = (next: { filter?: SupplierFilter; q?: string }) => {
    const merged = { filter, q: query, ...next }
    void navigate({
      to: supplierId ? `/suppliers/${supplierId}` : '/suppliers',
      search: {
        ...(merged.filter !== 'all' ? { filter: merged.filter } : {}),
        ...(merged.q ? { q: merged.q } : {}),
      },
      replace: true,
    })
  }
  const toggleFilter = (value: SupplierFilter) =>
    setSearch({ filter: filter === value ? 'all' : value })
  const openCatalog = (category?: string) =>
    void navigate({ to: '/suppliers/catalog', search: category ? { category } : {} })
  const [adding, setAdding] = useState(false)
  const onAdd = () => setAdding(true)
  const onOpen = (supplier: Supplier, tab?: SheetTab) =>
    void navigate({
      to: `/suppliers/${supplier.id}`,
      search: { ...listSearch, ...(tab ? { tab } : {}) },
    })
  const closeSupplier = () => void navigate({ to: '/suppliers', search: listSearch })
  const openSupplier = data.suppliers.find((supplier) => supplier.id === supplierId)
  const sheetTab = sheetTabs.find((value) => value === search.tab)

  const suppliers = data.suppliers

  if (suppliers.length === 0)
    return (
      <SuppliersPage tab="mine">
        <EmptyState
          icon={<img src={suppliersIllustration} alt="" className="h-28 w-32" />}
          title="Zatím tu nejsou žádní dodavatelé"
          description={
            canEdit
              ? 'Přidejte ty, se kterými už jednáte, nebo si vyberte z katalogu. Uvidíte tu jejich stav i kontakty.'
              : 'Až někdo přidá prvního dodavatele, zobrazí se tady.'
          }
          actions={
            canEdit ? (
              <div className="flex flex-col items-center gap-5">
                <div className="flex flex-wrap justify-center gap-3">
                  <Button appearance="outline" tone="neutral" onClick={() => openCatalog()}>
                    <BookOpen aria-hidden="true" className="size-4" /> Projít katalog
                  </Button>
                  <Button onClick={onAdd}>
                    <Plus aria-hidden="true" className="size-4" /> Přidat dodavatele
                  </Button>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <p className="text-xs text-muted-foreground">Nebo začněte tím nejdůležitějším:</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {starterCategories.map((category) => (
                      <Button
                        key={category}
                        appearance="ghost"
                        tone="neutral"
                        size="sm"
                        onClick={() => openCatalog(category)}
                      >
                        {categoryLabel(category)}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            ) : null
          }
        />
        {adding ? <AddSupplierSheet onClose={() => setAdding(false)} /> : null}
      </SuppliersPage>
    )

  const counts = Object.fromEntries(
    supplierFilters.map((value) => [
      value,
      suppliers.filter((supplier) => matchesFilter(supplier, value)).length,
    ]),
  ) as Record<SupplierFilter, number>
  const active = suppliers.length - counts.cancelled
  const agreed = suppliers.filter(isAgreed).length
  const visible = suppliers.filter(
    (supplier) => matchesFilter(supplier, filter) && matchesSearch(supplier, query),
  )

  return (
    <SuppliersPage
      tab="mine"
      actions={
        canEdit ? (
          <Button onClick={onAdd}>
            <Plus aria-hidden="true" className="size-4" /> Přidat dodavatele
          </Button>
        ) : null
      }
    >
      <StatSummary
        groups={[
          {
            title: 'Domluvení dodavatelé',
            value: `${agreed} z ${active}`,
            progress: {
              value: agreed,
              max: Math.max(active, 1),
              tone: 'success',
              label: `Domluveno ${agreed} z ${active} dodavatelů`,
            },
            tiles: [
              {
                label: 'Podepsáno',
                value: counts.booked,
                caption: 'Smlouva je hotová',
                tone: 'success',
                onSelect: () => toggleFilter('booked'),
                active: filter === 'booked',
              },
              {
                label: 'Domluveno',
                value: counts.agreed,
                caption: 'Smlouva zatím chybí',
                tone: counts.agreed > 0 ? 'warning' : 'default',
                onSelect: () => toggleFilter('agreed'),
                active: filter === 'agreed',
              },
            ],
          },
          {
            title: 'Zatím nedomluvení',
            tiles: [
              {
                label: 'Poptáváme',
                value: counts.inquiry,
                caption: 'Vybíráte nebo čekáte na nabídku',
                onSelect: () => toggleFilter('inquiry'),
                active: filter === 'inquiry',
              },
              {
                label: 'Zrušeno',
                value: counts.cancelled,
                caption: 'Spolupráce skončila',
                onSelect: () => toggleFilter('cancelled'),
                active: filter === 'cancelled',
              },
            ],
          },
        ]}
      />

      {canEdit ? (
        <PromptList
          prompts={supplierPrompts(data).map((prompt) => ({
            ...prompt,
            action: (
              <PromptAction
                action={prompt.action}
                onTask={(title) => addTask(title, { labels: ['Dodavatelé'] })}
                onCatalog={openCatalog}
                onNavigate={(to) => void navigate({ to })}
                onOpen={(id) => {
                  const supplier = suppliers.find((item) => item.id === id)
                  if (supplier) onOpen(supplier, 'contacts')
                }}
              />
            ),
          }))}
          onHide={(code) =>
            update((current) => ({ ...current, hiddenPrompts: [...current.hiddenPrompts, code] }))
          }
        />
      ) : null}

      <FilterBar
        filters={supplierFilters.map((value) => ({
          value,
          label: filterLabels[value],
          count: counts[value],
        }))}
        value={filter}
        onChange={(value) => setSearch({ filter: value })}
        search={{
          value: query,
          onChange: (value) => setSearch({ q: value }),
          placeholder: 'Hledat dodavatele',
        }}
      />

      <DataTable
        aria-label="Moji dodavatelé podle kategorií"
        columns={supplierColumns(data)}
        getRowId={(supplier) => supplier.id}
        onRowClick={(supplier) => onOpen(supplier)}
        rowMuted={(supplier) => supplier.status === 'cancelled'}
        groups={categoryGroups(visible)}
        empty={
          <div className="space-y-3">
            <p className="font-semibold text-foreground">Žádní dodavatelé v tomto přehledu</p>
            <p className="text-sm">Zkuste hledat jinak nebo zobrazit všechny dodavatele.</p>
            <Button
              appearance="outline"
              tone="neutral"
              size="sm"
              onClick={() => setSearch({ filter: 'all', q: '' })}
            >
              Zobrazit všechny dodavatele
            </Button>
          </div>
        }
      />
      {openSupplier ? (
        <SupplierSheet
          key={openSupplier.id}
          supplier={openSupplier}
          initialTab={sheetTab}
          onClose={closeSupplier}
        />
      ) : null}
      {adding ? <AddSupplierSheet onClose={() => setAdding(false)} /> : null}
    </SuppliersPage>
  )
}

/** One group per category, so candidates for the same job sit together. */
function categoryGroups(suppliers: Supplier[]): TableGroup<Supplier>[] {
  const rank = (category: string) => {
    const index = categoryOrder.indexOf(category)
    return index === -1 ? categoryOrder.length : index
  }
  const categories = [...new Set(suppliers.map((supplier) => supplier.category))].sort(
    (a, b) => rank(a) - rank(b),
  )
  return categories.map((category) => {
    const rows = suppliers.filter((supplier) => supplier.category === category)
    const chosen = rows.find(isAgreed)
    return {
      id: category,
      label: categoryLabel(category),
      meta:
        rows.length > 1
          ? `${rows.length} ${plural(rows.length, 'dodavatel', 'dodavatelé', 'dodavatelů')}${chosen ? '' : ' · zatím nevybráno'}`
          : undefined,
      rows,
      collapsible: true,
    }
  })
}

function supplierColumns(data: WeddingData): Column<Supplier>[] {
  return [
    {
      key: 'name',
      header: 'Dodavatel',
      width: 'minmax(0,1fr)',
      primary: true,
      cell: (supplier) => {
        const contact = supplier.contacts[0]
        return (
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate font-medium">{supplier.name}</span>
            <span className="truncate text-xs font-normal text-muted-foreground">
              {contact
                ? [contact.name, contact.role].filter(Boolean).join(' · ')
                : 'Bez kontaktní osoby'}
            </span>
          </span>
        )
      },
    },
    {
      key: 'contact',
      header: 'Kontakt',
      width: '12rem',
      interactive: true,
      cell: (supplier) => {
        const contact = supplier.contacts[0]
        if (!contact || (!contact.phone && !contact.email)) return null
        return (
          <span className="flex min-w-0 flex-col items-end gap-0.5 text-sm md:items-start">
            {contact.phone ? (
              <a href={`tel:${contact.phone.replaceAll(' ', '')}`} className="hover:underline">
                {contact.phone}
              </a>
            ) : null}
            {contact.email ? (
              <a
                href={`mailto:${contact.email}`}
                className="max-w-full truncate text-xs text-muted-foreground hover:underline"
              >
                {contact.email}
              </a>
            ) : null}
          </span>
        )
      },
    },
    {
      key: 'usage',
      header: 'Kde figuruje',
      width: '8rem',
      interactive: true,
      mobile: 'hide',
      cell: (supplier) => <UsageIcons supplier={supplier} data={data} />,
    },
    {
      key: 'status',
      header: 'Stav',
      width: '12rem',
      align: 'end',
      cell: (supplier) => <SupplierStatus status={supplier.status} />,
    },
  ]
}

/** Section, schedule and budget as quiet icons; the tooltip says where. */
function UsageIcons({ supplier, data }: { supplier: Supplier; data: WeddingData }) {
  const navigate = useNavigate()
  const usage = supplierUsage(supplier, data)
  if (usage.length === 0) return null
  return (
    <span className="flex items-center gap-1">
      {usage.map((item) => {
        const config = linkedModuleConfig(item.module)
        const Icon = config.icon
        const label =
          item.module === 'schedule' || item.module === 'budget' ? item.label : config.label
        return (
          <Tooltip key={item.module}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => void navigate({ to: config.to })}
                className="grid size-7 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
              >
                <Icon aria-hidden="true" className="size-4" />
                <span className="sr-only">{label}</span>
              </button>
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
          </Tooltip>
        )
      })}
    </span>
  )
}

function PromptAction({
  action,
  onTask,
  onCatalog,
  onNavigate,
  onOpen,
}: {
  action: SupplierPromptAction
  onTask: (title: string) => void
  onCatalog: (category: string) => void
  onNavigate: (to: string) => void
  onOpen: (supplierId: string) => void
}) {
  const props = { size: 'sm', appearance: 'outline', tone: 'neutral' } as const
  if (action.kind === 'catalog')
    return (
      <Button {...props} onClick={() => onCatalog(action.category)}>
        Najít v katalogu
        <ArrowRight aria-hidden="true" className="size-3.5" />
      </Button>
    )
  if (action.kind === 'budget')
    return (
      <Button {...props} onClick={() => onNavigate('/budget')}>
        Otevřít rozpočet
        <ArrowRight aria-hidden="true" className="size-3.5" />
      </Button>
    )
  if (action.kind === 'schedule')
    return (
      <Button {...props} onClick={() => onNavigate('/schedule')}>
        Otevřít harmonogram
        <ArrowRight aria-hidden="true" className="size-3.5" />
      </Button>
    )
  if (action.kind === 'open')
    return (
      <Button {...props} onClick={() => onOpen(action.supplierId)}>
        Doplnit kontakt
      </Button>
    )
  return (
    <Button {...props} onClick={() => onTask(action.title)}>
      <Plus aria-hidden="true" className="size-3.5" />
      Přidat úkol
    </Button>
  )
}

export function CatalogScreen() {
  return (
    <SuppliersPage tab="catalog">
      <CatalogContent />
    </SuppliersPage>
  )
}
